import { isSsr } from '@/utilities/Utilities';
import axios from 'axios';
import store from '@/store';
import { serialize } from 'object-to-formdata';
import { CampaignState } from '@/models/CampaignModel';

// eslint-disable-next-line @typescript-eslint/ban-types
export default function useAxios<ResponseData>(url: string, data?: object, headers?: Record<string, string>) {
  const fetchData = async (): Promise<ResponseData> => {
    // For SSR we need to proxy the 'Cookie' header so that requests are seen as signed in.
    const cookies = isSsr() ? store.getters['utility/cookies'] : null;

    return (
      await axios.get<ResponseData>(url, {
        url,
        timeout: 20000, // We need to specify a timeout for handling any exceptions that might happen on the server. The server will throw an exception after 28secs. If nothing is specified where, it will ping the server in a duration 60 seconds, here we are limiting it to 20s
        params: data,
        headers: cookies
          ? {
              Cookie: cookies
            }
          : {}
      })
    ).data;
  };

  const fetchDataClean = async (): Promise<ResponseData> => {
    return (
      await axios.get<ResponseData>(url, {
        params: data
      })
    ).data;
  };

  const postData = async (): Promise<ResponseData> => {
    const campaignState = store.getters['campaign/state'] as CampaignState;
    return (
      await axios.post<ResponseData>(url, data, {
        headers: {
          ...(campaignState.edit?.csrfToken && { ['X-CSRF-TOKEN']: campaignState.edit.csrfToken })
        }
      })
    ).data;
  };

  const postDataFormData = async (): Promise<ResponseData> => {
    const campaignState = store.getters['campaign/state'] as CampaignState;
    return (
      await axios.post<ResponseData>(url, serialize(data, { indices: true }), {
        headers: campaignState.edit?.csrfToken
          ? {
              ['X-CSRF-TOKEN']: campaignState.edit.csrfToken,
              'Content-Type': 'application/x-www-form-urlencoded'
            }
          : {
              'Content-Type': 'application/x-www-form-urlencoded'
            }
      })
    ).data;
  };

  const postDeleteFormData = async (): Promise<ResponseData> => {
    const campaignState = store.getters['campaign/state'] as CampaignState;

    return (
      await axios.delete(url, {
        data: data,
        headers: campaignState.edit?.csrfToken
          ? {
              ['X-CSRF-TOKEN']: campaignState.edit.csrfToken
            }
          : {}
      })
    ).data;
  };

  const deleteData = async (): Promise<ResponseData> => {
    const campaignState = store.getters['campaign/state'] as CampaignState;

    return (
      await axios.delete(url, {
        headers: campaignState.edit?.csrfToken
          ? {
              ['X-CSRF-TOKEN']: campaignState.edit.csrfToken
            }
          : {}
      })
    ).data;
  };

  const putData = async (): Promise<ResponseData> => {
    return (
      await axios.put(url, data, {
        headers: headers
      })
    ).data;
  };

  return {
    postDataFormData,
    postData,
    fetchData,
    fetchDataClean,
    postDeleteFormData,
    putData,
    deleteData
  };
}
